import { Controller } from "@hotwired/stimulus"
import { visit } from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["message"]
  static values = {
    startTime: Number,
    messageStages: Array,
    refreshTimeout: { type: Number, default: 10 }, // Default 10 seconds timeout for refresh
    refreshUrl: String // URL to refresh to, passed from the view
  }

  connect() {
    this.startTimeValue = Date.now()
    this.lastMessageIndex = -1 // Track which message we're showing
    this.hasScheduledRefresh = false
    
    // Get message stages from data attribute
    if (!this.hasMessageStagesValue) {
      // Default message stages if not provided
      this.messageStagesValue = [
        { seconds: 0, message: "Thinking..." },
        { seconds: 5, message: "Still thinking..." },
        { seconds: 10, message: "Processing your request..." },
        { seconds: 20, message: "This is taking longer than usual..." },
        { seconds: 30, message: "Almost there..." },
        { seconds: 45, message: "Working on a detailed response..." },
        { seconds: 60, message: "Thank you for your patience..." }
      ]
    }
    
    // Sort message stages by seconds in ascending order
    this.messageStagesValue.sort((a, b) => a.seconds - b.seconds)
    
    this.updateLoadingMessage()
    this.interval = setInterval(() => this.updateLoadingMessage(), 2000)
  }

  disconnect() {
    this.clearAllTimers()
  }
  
  clearAllTimers() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
    
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  updateLoadingMessage() {
    const elapsedSeconds = Math.floor((Date.now() - this.startTimeValue) / 1000)
    let message = this.messageStagesValue[0].message // Default to first message
    let currentMessageIndex = 0
    
    // Find the appropriate message based on elapsed time
    for (let i = this.messageStagesValue.length - 1; i >= 0; i--) {
      if (elapsedSeconds >= this.messageStagesValue[i].seconds) {
        message = this.messageStagesValue[i].message
        currentMessageIndex = i
        break
      }
    }

    this.messageTarget.textContent = message
    
    // If we've reached the last message and haven't scheduled a refresh yet
    if (currentMessageIndex === this.messageStagesValue.length - 1 && 
        !this.hasScheduledRefresh) {
      this.scheduleRefresh()
    }
    
    // Track that we've shown this message
    this.lastMessageIndex = currentMessageIndex
  }
  
  scheduleRefresh() {
    this.hasScheduledRefresh = true
    console.log(`Scheduling page refresh in ${this.refreshTimeoutValue} seconds`)
    
    this.refreshTimer = setTimeout(() => {
      console.log('Refreshing page to check for response...')
      this.refreshPage()
    }, this.refreshTimeoutValue * 1000)
  }
  
  refreshPage() {
    // Store a timestamp in sessionStorage to prevent infinite refresh loops
    const lastRefreshKey = 'lastCompanionLoadingRefreshTime'
    const now = Date.now()
    const lastRefresh = parseInt(sessionStorage.getItem(lastRefreshKey) || '0')
    
    // Only refresh if we haven't refreshed in the last 5 seconds
    // This prevents potential refresh loops
    if (now - lastRefresh > 5000) {
      sessionStorage.setItem(lastRefreshKey, now.toString())
      
      // Use Turbo to visit the companion show page using the URL from the view
      visit(this.refreshUrlValue)
    } else {
      console.log('Skipping refresh - too soon after previous refresh')
    }
  }
}
